import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Tag, ArrowLeft, BookOpen } from 'lucide-react';
import { getAllPosts } from './blogPosts';
import { BlogPreviewCard } from './BlogPreviewCard';
import { SEOHead } from '../components/SEOHead';
import { useLocaleContext } from '../context/LocaleContext';
import { getCategoryBySlug, getCategoryByLocalizedSlug, BLOG_CATEGORIES } from './categories';
import { CategoryIntro } from '../components/CategoryIntro';
import { CategoryRelatedContent } from '../components/CategoryRelatedContent';
import { getCategoryEnhancedDescription } from '../data/categoryDescriptions';
import { buildCategoryUrl } from './categorySlugMapping';

export const CategoryPage: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { locale } = useLocaleContext();

  // Try localized slug lookup first, fallback to English slug
  const category = categorySlug
    ? (getCategoryByLocalizedSlug(categorySlug, locale) || getCategoryBySlug(categorySlug))
    : undefined;
  const allPosts = getAllPosts(locale);

  // Filter posts by category
  const categoryPosts = category
    ? allPosts.filter((post) => post.category === category.id)
    : [];

  // Get category label and description in current locale
  const categoryLabel = category?.label[locale as keyof typeof category.label] || category?.label.en || 'Category';
  const categoryDescription = category?.description[locale as keyof typeof category.description] || category?.description.en || '';

  // Get enhanced SEO data
  const enhanced = category ? getCategoryEnhancedDescription(category.id) : undefined;
  const localeKey = locale as 'en' | 'de' | 'pt-BR' | 'ms-MY';
  const seoTitle = enhanced?.seoTitle[localeKey] || enhanced?.seoTitle.en || `${categoryLabel} - AI Watermark Removal Blog`;
  const seoDescription = enhanced?.seoDescription[localeKey] || enhanced?.seoDescription.en || categoryDescription;

  useEffect(() => {
    // Guard against SSR
    if (typeof window === 'undefined' || !category) return;

    // Build localized category URL for structured data
    const categoryUrl = buildCategoryUrl(category.id, locale);
    const fullUrl = `https://gpt-watermark-remover.com${categoryUrl}`;

    // Add JSON-LD structured data for CollectionPage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': `${categoryLabel} - AI Watermark Removal Blog`,
      'description': categoryDescription,
      'url': fullUrl,
      'publisher': {
        '@type': 'Organization',
        'name': 'GROW FAST LTD.',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://gpt-watermark-remover.com/logo.png'
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [category, locale, categoryLabel, categoryDescription]);

  if (!category) {
    return (
      <>
        <SEOHead
          title="Category Not Found | GPT Watermark Remover"
          description="The requested category was not found."
        />
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-charcoal-900 dark:text-white mb-4">
              Category Not Found
            </h1>
            <p className="text-xl text-neutral-600 dark:text-neutral-300 mb-8">
              The category you're looking for doesn't exist.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
      />

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-8">
          <Link to="/blog" className="hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
            Blog
          </Link>
          <span>/</span>
          <span className="text-charcoal-900 dark:text-white font-medium">
            {categoryLabel}
          </span>
        </nav>

        {/* Enhanced Category Introduction */}
        {category && (
          <CategoryIntro categoryId={category.id} locale={locale} />
        )}

        {/* Article Count Badge */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-charcoal-800 rounded-lg">
            <BookOpen className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
            <span className="text-neutral-700 dark:text-neutral-300 font-medium">
              {categoryPosts.length} {categoryPosts.length === 1 ? 'article' : 'articles'}
            </span>
          </div>
        </div>

        {/* Related Content - Shown Before Posts for SEO */}
        {category && (
          <CategoryRelatedContent categoryId={category.id} locale={locale} />
        )}

        {/* Blog Posts Grid */}
        {categoryPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {categoryPosts.map((post) => (
              <BlogPreviewCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 mb-16">
            <BookOpen className="w-16 h-16 mx-auto text-neutral-400 dark:text-neutral-600 mb-4" />
            <h2 className="text-2xl font-bold text-neutral-600 dark:text-neutral-400 mb-2">
              No articles in this category yet
            </h2>
            <p className="text-neutral-500 dark:text-neutral-500 mb-8">
              Check back soon for new content.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Browse All Articles
            </Link>
          </div>
        )}

        {/* All Categories Section */}
        <div className="bg-neutral-100 dark:bg-charcoal-800/50 rounded-xl p-8 border border-neutral-200 dark:border-charcoal-700">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-6">
            Browse All Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {BLOG_CATEGORIES.map((cat) => {
              const catLabel = cat.label[locale as keyof typeof cat.label] || cat.label.en;
              const catPosts = allPosts.filter((post) => post.category === cat.id);
              const isCurrentCategory = cat.id === category.id;

              return (
                <Link
                  key={cat.id}
                  to={buildCategoryUrl(cat.id, locale)}
                  className={`block p-4 rounded-lg transition-all ${
                    isCurrentCategory
                      ? 'bg-accent-600 text-white shadow-lg'
                      : 'bg-white dark:bg-charcoal-700 hover:bg-neutral-50 dark:hover:bg-charcoal-600 border border-neutral-200 dark:border-charcoal-600'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className={`font-semibold ${
                      isCurrentCategory
                        ? 'text-white'
                        : 'text-charcoal-900 dark:text-white'
                    }`}>
                      {catLabel}
                    </h3>
                    <span className={`text-sm px-2 py-1 rounded ${
                      isCurrentCategory
                        ? 'bg-white/20 text-white'
                        : 'bg-neutral-100 dark:bg-charcoal-800 text-neutral-600 dark:text-neutral-400'
                    }`}>
                      {catPosts.length}
                    </span>
                  </div>
                  <p className={`text-sm ${
                    isCurrentCategory
                      ? 'text-white/90'
                      : 'text-neutral-600 dark:text-neutral-400'
                  }`}>
                    {cat.description[locale as keyof typeof cat.description] || cat.description.en}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
